import { useEffect, useRef, useState } from 'react';
import { UseFocusTrapSettings } from './useFocusTrap.typed';

// TODO: Handle multiple focus traps at once similar to useClickOutside
// TODO: Handle returnFocus being deleted
// TODO: use document.activeElement to capture returnFocus
const KEYCODE_TAB = 9;
const FOCUSABLE_ELEMENTS =
  'button:not(:disabled), [href]:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"]):not(:disabled)';

export const useFocusTrap = (props: UseFocusTrapSettings) => {
  const { container } = props;
  const [isTrapped, setIsTrapped] = useState(false);
  const lastActiveElement = useRef<HTMLElement | null>(null);

  const isFocussed = (element: HTMLElement) => {
    return document.activeElement === element;
  };

  const getFocusableElement = (
    elements: NodeListOf<Element>,
    isShiftPressed: boolean,
  ) => {
    const firstFocusableElement = elements[0] as HTMLElement;
    const lastFocusableElement = elements[elements.length - 1] as HTMLElement;

    if (isShiftPressed && isFocussed(firstFocusableElement)) {
      return lastFocusableElement;
    } else if (!isShiftPressed && isFocussed(lastFocusableElement)) {
      return firstFocusableElement;
    }
    return null;
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const isTabPressed = event.key === 'Tab' || event.keyCode === KEYCODE_TAB;

    if (!isTabPressed || !container) {
      return;
    }

    const focusableElements = container.querySelectorAll(FOCUSABLE_ELEMENTS);
    const focusableElement = getFocusableElement(
      focusableElements,
      event.shiftKey,
    );

    if (focusableElement) {
      focusableElement.focus();
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (isTrapped) {
      window.addEventListener('keydown', onKeyDown);
    } else {
      window.removeEventListener('keydown', onKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrapped]);

  const trapFocus = () => {
    if (container) {
      lastActiveElement.current = document.activeElement as HTMLElement;
      const elements = container.querySelectorAll(FOCUSABLE_ELEMENTS);
      if (elements && elements[0]) {
        (elements[0] as HTMLElement).focus();
      }
      setIsTrapped(true);
    }
  };
  const unTrapFocus = (element: HTMLElement | null | undefined) => {
    if (element) {
      element.focus();
    } else if (lastActiveElement.current) {
      lastActiveElement.current.focus();
      lastActiveElement.current = null;
    }
    setIsTrapped(false);
  };

  return { trapFocus, unTrapFocus };
};
